import { useState } from 'preact/hooks';

interface Props {
  title: string;
  slideCount: number;
  slides: string[];
}

/**
 * Inline card shown in chat when a ```presentation fence is detected.
 * Tap "Present" to open full-screen Reveal.js viewer.
 */
export function PresentationCard({ title, slideCount, slides }: Props) {
  const [viewerOpen, setViewerOpen] = useState(false);

  const openViewer = () => {
    setViewerOpen(true);
    window.history.pushState({ presentation: true }, '', '#presentation');
  };

  return (
    <>
      <div class="presentation-card">
        <div class="presentation-card-icon">📊</div>
        <div class="presentation-card-info">
          <div class="presentation-card-title">{title}</div>
          <div class="presentation-card-meta">{slideCount} slide{slideCount !== 1 ? 's' : ''}</div>
        </div>
        <button class="presentation-card-btn" onClick={openViewer}>
          ▶ Present
        </button>
      </div>
      {viewerOpen && (
        <PresentationViewer
          slides={slides}
          title={title}
          onClose={() => {
            setViewerOpen(false);
            window.history.back();
          }}
        />
      )}
    </>
  );
}

/**
 * Full-screen slide viewer using simple CSS-based slide navigation.
 * Lighter than Reveal.js — just vanilla slide transitions with keyboard/swipe/tap.
 */
function PresentationViewer({
  slides,
  title,
  onClose,
}: {
  slides: string[];
  title: string;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => Math.max(0, c - 1));
  const next = () => setCurrent((c) => Math.min(slides.length - 1, c + 1));

  // Keyboard navigation
  const handleKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    else if (e.key === 'Escape') onClose();
  };

  return (
    <div
      class="pres-viewer-overlay"
      onKeyDown={handleKey as any}
      tabIndex={0}
      ref={(el) => el?.focus()}
    >
      <div class="pres-viewer-header">
        <button class="pres-viewer-close" onClick={onClose}>✕ Close</button>
        <span class="pres-viewer-title">{title}</span>
        <span class="pres-viewer-counter">{current + 1} / {slides.length}</span>
      </div>
      <div class="pres-viewer-stage" onClick={next}>
        <div
          class="pres-viewer-slide md-content"
          dangerouslySetInnerHTML={{ __html: slides[current] || '' }}
        />
      </div>
      <div class="pres-viewer-nav">
        <button onClick={prev} disabled={current === 0}>← Prev</button>
        <div class="pres-viewer-dots">
          {slides.map((_, i) => (
            <span
              key={i}
              class={`pres-dot${i === current ? ' pres-dot--active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setCurrent(i); }}
            />
          ))}
        </div>
        <button onClick={next} disabled={current === slides.length - 1}>Next →</button>
      </div>
    </div>
  );
}
